import { ReactElement, useMemo } from 'react'

import { MenuProps } from 'antd'
import { type ItemType } from 'antd/es/menu/interface'
import classNames from 'classnames'
import isEmpty from 'lodash/isEmpty'
import { useSelector } from 'react-redux'
import { Link, matchPath, useLocation } from 'react-router-dom'

import { Icon } from 'components/core'
import {
    CloseIcon,
    ExternalLinkIcon,
    LockedLockIcon,
    PaintBrushIcon,
    PlayButtonIcon,
} from 'components/Icons'
import { useAgentsQuotaQuery } from 'features/Agents/useAgentsQuotaQuery'
import {
    userHasAmazonAdvertisingPermissions,
    userHasDashboardsOnlyPermissions,
    userHasSovReadPermissions,
} from 'helpers/featurePermissions'
import { getOrganizationTheme } from 'helpers/organizationTheme'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useGatedFeatures } from 'hooks/useGatedFeatures'
import useLocalStorage from 'hooks/useLocalStorage'
import { getPath } from 'routing/pagePaths'
import { selectIsSidebarCollapsed } from 'selectors/ui'
import { Organization, RootReduxState, UserPermission } from 'types'
import {
    getVisibleNavItems,
    NavItemKey,
} from 'views/AppLayout/SideMenu/navigation'

import DarkMenu from './DarkMenu'
import * as styles from './styles.module.scss'

const TOP_LEVEL_ITEMS: NavItemKey[] = [
    'homePage',
    'workflows',
    'aiArtifacts',
    'aiAgents',
    'customDashboards',
]

const MARKET_TRENDS_ITEMS: NavItemKey[] = [
    'marketAnalysis',
    'categoryPerformance',
    'marketBrands',
    'marketProducts',
    'marketSellers',
    'marketSegment',
]

const BRAND_MANAGEMENT_ITEMS: NavItemKey[] = [
    'distributionAnalysis',
    'productCatalog',
]

const DIGITAL_SHELF_ITEMS: NavItemKey[] = [
    'shareOfVoice',
    'keywordResearch',
    'keywordResearchV2',
    'keywordLists',
]

const TOOLS_ITEMS: NavItemKey[] = ['labels', 'settings']

export const AMAZON_ITEMS: NavItemKey[] = [
    'amazonCampaigns',
    'amazonDsp',
    'automation',
]

interface SideMenuMainNavigationProps {
    currentOrganization?: Organization
    hasNoIntegrations: boolean
    featurePermissions: UserPermission[]
}

const SideMenuMainNavigation = ({
    currentOrganization,
    hasNoIntegrations,
    featurePermissions,
}: SideMenuMainNavigationProps): ReactElement => {
    const location = useLocation()

    const featureFlags = useFeatureFlags()
    const gatedFeatures = useGatedFeatures()
    // React Query keeps the last access level through refetches, so the item never flashes.
    const { data: agentsQuota } = useAgentsQuotaQuery({
        enabled: Boolean(featureFlags.scheduledAgents),
    })
    const agentsAccess = agentsQuota?.access

    const isSidebarCollapsed = useSelector((state: RootReduxState) =>
        selectIsSidebarCollapsed(state)
    )

    const dashboardOnlyExperience =
        userHasDashboardsOnlyPermissions(featurePermissions)
    const amazonAdPermissions =
        userHasAmazonAdvertisingPermissions(featurePermissions)
    const sovReadPermissions = userHasSovReadPermissions(featurePermissions)
    const visibleNavItems = useMemo(
        () =>
            getVisibleNavItems(
                featurePermissions,
                featureFlags,
                gatedFeatures,
                agentsAccess
            ),
        [featureFlags, featurePermissions, gatedFeatures, agentsAccess]
    )

    const selectedMenuKey = useMemo(() => {
        const { pathname } = location
        const items = Object.values(visibleNavItems)

        // Try exact path match
        const exactMatch = items.find(({ path }) =>
            matchPath({ path, end: true }, pathname)
        )
        if (exactMatch) {
            return exactMatch.key as string
        }

        // If unsuccessful, try longest path match
        const matchesByLength = items
            .filter(({ path }) => matchPath({ path, end: false }, pathname))
            .toSorted((a, b) => b.path.length - a.path.length)

        // Home page always matches, so we're looking for another match
        return matchesByLength.length > 1
            ? (matchesByLength[0].key as string)
            : null
    }, [location, visibleNavItems])
    const defaultOpenKeys = !isSidebarCollapsed
        ? ['marketTrends', 'brandManagement', 'digitalShelf', 'tools', 'amazon']
        : []
    const [persistedOpenKeys, setPersistedOpenKeys] = useLocalStorage<string[]>(
        'SideMenuOpenKeys',
        [...defaultOpenKeys]
    )
    const theme = getOrganizationTheme(currentOrganization)
    const extraLinks = theme?.extra_links ?? []

    const topLevelItems = TOP_LEVEL_ITEMS.map((item) => visibleNavItems[item])

    const marketTrendsChildrenItems = MARKET_TRENDS_ITEMS.map(
        (item) => visibleNavItems[item]
    )

    const marketTrendsItems: ItemType[] = [
        {
            key: 'marketTrends',
            icon: null,
            label: <span>Market Trends</span>,
            children: marketTrendsChildrenItems,
        },
    ]

    const brandManagementChildrenItems = BRAND_MANAGEMENT_ITEMS.map(
        (item) => visibleNavItems[item]
    )

    const brandManagementItems: ItemType[] = [
        {
            key: 'brandManagement',
            icon: null,
            label: <span>Brand Management</span>,
            children: brandManagementChildrenItems,
        },
    ]

    const digitalShelfChildrenItems = DIGITAL_SHELF_ITEMS.map(
        (item) => visibleNavItems[item]
    )

    const digitalShelfItems: ItemType[] = [
        {
            key: 'digitalShelf',
            icon: null,
            label: <span>Digital Shelf</span>,
            children: digitalShelfChildrenItems,
        },
    ]

    const toolsChildrenItems = TOOLS_ITEMS.map((item) => visibleNavItems[item])

    const toolsItems: ItemType[] = [
        {
            key: 'tools',
            icon: null,
            label: <span>Tools</span>,
            children: toolsChildrenItems,
        },
    ]

    const amazonChildrenItems = AMAZON_ITEMS.map(
        (item) => visibleNavItems[item]
    )

    const amazonItems: ItemType[] = [
        {
            key: 'amazon',
            icon: hasNoIntegrations ? <LockedLockIcon /> : null,
            label: <span>Advertising</span>,
            children: amazonChildrenItems.map(
                (item) =>
                    ({
                        ...item,
                        disabled: hasNoIntegrations,
                    }) as ItemType
            ),
        },
    ]

    const divider: ItemType[] = [
        {
            key: 'extraLinksDivider',
            label: (
                <div
                    className={classNames(styles.sectionDivider, {
                        [styles.sectionDividerCollapsed]: isSidebarCollapsed,
                    })}
                />
            ),
        },
    ]

    const extraItems: ItemType[] = extraLinks.map(({ src, label }, idx) => ({
        key: `extraLink_${idx}`,
        label: !isSidebarCollapsed ? (
            <a target="_blank" rel="noopener noreferrer" href={src}>
                <ExternalLinkIcon />
                {!isSidebarCollapsed && <span>{label}</span>}
            </a>
        ) : (
            label
        ),
    }))

    const sectionDivider: ItemType = {
        type: 'divider',
    }

    // Build flat menu structure for collapsed state
    const buildFlatMenu = (): ItemType[] => {
        const items: ItemType[] = [...topLevelItems]

        if (!dashboardOnlyExperience) {
            if (marketTrendsChildrenItems.length > 0) {
                items.push(sectionDivider, ...marketTrendsChildrenItems)
            }
            if (brandManagementChildrenItems.length > 0) {
                items.push(sectionDivider, ...brandManagementChildrenItems)
            }
            if (sovReadPermissions && digitalShelfChildrenItems.length > 0) {
                items.push(sectionDivider, ...digitalShelfChildrenItems)
            }
            if (amazonAdPermissions && amazonChildrenItems.length > 0) {
                items.push(
                    sectionDivider,
                    ...amazonChildrenItems.map(
                        (item) =>
                            ({
                                ...item,
                                disabled: hasNoIntegrations,
                            }) as ItemType
                    )
                )
            }
            if (toolsChildrenItems.length > 0) {
                items.push(sectionDivider, ...toolsChildrenItems)
            }
        }

        if (!isEmpty(extraLinks)) {
            items.push(...divider, ...extraItems)
        }

        return items
    }

    // Build grouped menu structure for expanded state
    const buildGroupedMenu = (): ItemType[] => {
        return [
            ...topLevelItems,
            ...(!dashboardOnlyExperience ? marketTrendsItems : []),
            ...(!dashboardOnlyExperience ? brandManagementItems : []),
            ...(!dashboardOnlyExperience && sovReadPermissions
                ? digitalShelfItems
                : []),

            ...(!dashboardOnlyExperience && amazonAdPermissions
                ? amazonItems
                : []),
            ...(!dashboardOnlyExperience ? toolsItems : []),
            ...(!isEmpty(extraLinks) ? divider : []),
            ...extraItems,
        ]
    }

    const menuItems: ItemType[] = isSidebarCollapsed
        ? buildFlatMenu()
        : buildGroupedMenu()

    const persistOpenKeys: MenuProps['onOpenChange'] = (openKeys) => {
        // do nothing if sidebar is collapsed, otherwise all menus would close when collapsing and expanding
        if (isSidebarCollapsed) {
            return
        }

        setPersistedOpenKeys(openKeys)
    }

    if (process.env.NODE_ENV === 'development') {
        const devToolsChildren: ItemType[] = [
            {
                key: 'resetUi',
                icon: <CloseIcon />,
                label: (
                    <span className={styles.navLabelText}>
                        Clear UI Settings
                    </span>
                ),
                onClick: () => {
                    Object.keys(localStorage)
                        .filter((key) => key.startsWith('persist:ui'))
                        .forEach((key) => localStorage.removeItem(key))
                    window.location.reload()
                },
            },
            {
                key: 'debugSandboxPage',
                icon: <PlayButtonIcon />,
                label: (
                    <Link to={getPath('debugSandboxPage')}>
                        <span className={styles.navLabelText}>Sandbox</span>
                    </Link>
                ),
            },
            {
                key: 'debugIconsPage',
                icon: <PaintBrushIcon />,
                label: (
                    <Link to={getPath('debugIconsPage')}>
                        <span className={styles.navLabelText}>All Icons</span>
                    </Link>
                ),
            },
            {
                key: 'debugColumnsPage',
                icon: <Icon name="TYPE" size={16} />,
                label: (
                    <Link to={getPath('debugColumnsPage')}>
                        <span className={styles.navLabelText}>
                            Column Definitions
                        </span>
                    </Link>
                ),
            },
            {
                key: 'debugMetricsPage',
                icon: <Icon name="HASH" size={16} />,
                label: (
                    <Link to={getPath('debugMetricsPage')}>
                        <span className={styles.navLabelText}>
                            Metric Definitions
                        </span>
                    </Link>
                ),
            },
        ]

        menuItems.push({
            key: 'devTools',
            icon: isSidebarCollapsed ? <Icon name="LAPTOP" size={16} /> : null,
            label: <span>Dev Tools</span>,
            children: devToolsChildren,
        })
    }

    return (
        <DarkMenu
            className={styles.forgeSideMenu}
            mode="inline"
            items={menuItems}
            defaultSelectedKeys={['homePage']}
            openKeys={persistedOpenKeys}
            onOpenChange={persistOpenKeys}
            selectedKeys={selectedMenuKey ? [selectedMenuKey] : []}
            expandIcon={({ isOpen }) => (
                <Icon
                    className={classNames(styles.navExpandIcon, {
                        [styles.navExpandIconOpen]: isOpen ?? false,
                        [styles.navExpandIconClosed]: !(isOpen ?? false),
                    })}
                    name="NAV_CHEVRON"
                    size={18}
                />
            )}
            inlineIndent={isSidebarCollapsed ? 0 : 24}
            inlineCollapsed={isSidebarCollapsed}
        />
    )
}

export default SideMenuMainNavigation
